// ** Type Imports
import { type UserDTO } from '@/entities/user'

type MaybeUser = UserDTO | null | undefined

// Mirrors the backend's messages, so its errors can be recognized by text.
export const FREE_PLAN_CLIENT_LIMIT_MESSAGE =
	'Free plan includes one client. Upgrade to add more.'

export const FREE_PLAN_CLIENT_DELETED_MESSAGE =
	'You deleted your free client. The free plan includes one client, so upgrade to add another.'

export const FREE_PLAN_ENDED_MESSAGE =
	'Your free plan has ended. Subscribe to reopen your client.'

export const getLaraLimitMessage = (limit: number): string =>
	`You've used all ${limit} free Agent Lara messages. Upgrade to keep chatting.`

/**
 * Whether the user may use the app: a real subscription, or the free plan.
 * Use this for routing, guards and hiding the app. Billing UI ("Manage
 * billing" etc.) keeps checking `activeSubscription` — free users have no
 * subscription to manage.
 */
export const hasAppAccess = (user: MaybeUser): boolean =>
	user?.activeSubscription === true || user?.freePlan?.active === true

/** On the free plan right now — the only time free-plan limits and UI apply. */
export const isOnFreePlan = (user: MaybeUser): boolean =>
	user?.activeSubscription !== true && user?.freePlan?.active === true

/** The free plan's one client is taken, so no more clients can be added. */
export const isFreePlanClientLimitReached = (user: MaybeUser): boolean =>
	isOnFreePlan(user) && user?.freePlan?.clientUsed === true

/** The free client was deleted — the slot stays used, so adding is blocked. */
export const isFreePlanClientDeleted = (user: MaybeUser): boolean =>
	isOnFreePlan(user) && user?.freePlan?.clientDeleted === true

/** Copy for the add-client upgrade prompt on the free plan. */
export const getFreePlanClientLimitMessage = (user: MaybeUser): string =>
	isFreePlanClientDeleted(user)
		? FREE_PLAN_CLIENT_DELETED_MESSAGE
		: FREE_PLAN_CLIENT_LIMIT_MESSAGE

/**
 * The 90 days ran out for someone who actually used the free plan (they have
 * its client) and never subscribed. Their data is kept until they subscribe.
 */
export const isFreePlanEnded = (user: MaybeUser): boolean =>
	user?.activeSubscription !== true &&
	user?.freePlan?.active === false &&
	user.freePlan.clientUsed

/** Agent Lara messages left on the free plan, or null when not limited. */
export const getLaraMessagesLeft = (user: MaybeUser): number | null => {
	if (!isOnFreePlan(user) || !user?.freePlan) return null

	const { laraMessagesLimit, laraMessagesUsed } = user.freePlan

	return Math.max(laraMessagesLimit - laraMessagesUsed, 0)
}

/** The backend's 400 for a second client on the free plan. */
export const isFreePlanClientLimitError = (message: unknown): boolean =>
	typeof message === 'string' &&
	message.toLowerCase().includes('free plan includes one client')

/** The backend's 401 once access ended mid-session (e.g. free plan expired). */
export const isNoSubscriptionError = (message: unknown): boolean =>
	typeof message === 'string' &&
	message.toLowerCase().includes('does not have active subscription')
